import { generateClient } from 'aws-amplify/data'
import { type Schema } from '../../amplify/data/resource'
import { fetchAuthSession } from 'aws-amplify/auth'

const client = generateClient<Schema>()

export interface RegistrationCode {
  id: string
  code: string
  userId: string
  clientId?: string
  locationId?: string
  deviceName?: string
  expiresAt?: string
  isUsed?: boolean
  createdAt?: string
  updatedAt?: string
}

export interface CreateRegistrationCodeRequest {
  deviceName?: string
  expirationHours?: number
  clientId?: string
  locationId?: string
}

class RegistrationService {
  /**
   * Generate a new registration code
   */
  async generateRegistrationCode(request: CreateRegistrationCodeRequest = {}): Promise<RegistrationCode> {
    try {
      const session = await fetchAuthSession()
      const userId = session.userSub

      if (!userId) {
        throw new Error('User not authenticated')
      }

      // Generate a random 6-character code
      const code = Math.random().toString(36).substring(2, 8).toUpperCase()
      
      // Calculate expiration time (default 24 hours)
      const expirationHours = request.expirationHours || 24
      const expiresAt = new Date()
      expiresAt.setHours(expiresAt.getHours() + expirationHours)

      const { data: registrationCode, errors } = await client.models.RegistrationCode.create({
        code,
        userId,
        deviceName: request.deviceName,
        clientId: request.clientId,
        locationId: request.locationId,
        expiresAt: expiresAt.toISOString(),
        isUsed: false,
      })

      if (errors) {
        console.error('Error creating registration code:', errors)
        throw new Error('Failed to create registration code')
      }

      return {
        id: registrationCode?.id ?? '',
        code: registrationCode?.code ?? '',
        userId: registrationCode?.userId ?? '',
        clientId: registrationCode?.clientId || undefined,
        locationId: registrationCode?.locationId || undefined,
        deviceName: registrationCode?.deviceName || undefined,
        expiresAt: registrationCode?.expiresAt || undefined,
        isUsed: registrationCode?.isUsed || false,
        createdAt: registrationCode?.createdAt || undefined,
        updatedAt: registrationCode?.updatedAt || undefined,
      }
    } catch (error) {
      console.error('Error in generateRegistrationCode:', error)
      throw error
    }
  }

  /**
   * Get all registration codes for the current user
   */
  async getRegistrationCodes(): Promise<RegistrationCode[]> {
    try {
      const session = await fetchAuthSession()
      const userId = session.userSub

      if (!userId) {
        throw new Error('User not authenticated')
      }

      const { data: codes, errors } = await client.models.RegistrationCode.listRegistrationCodeByUserIdAndCreatedAt({
        userId,
      })

      if (errors) {
        console.error('Error fetching registration codes:', errors)
        throw new Error('Failed to fetch registration codes')
      }

      return codes.map(code => ({
        id: code.id,
        code: code.code,
        userId: code.userId,
        clientId: code.clientId || undefined,
        locationId: code.locationId || undefined,
        deviceName: code.deviceName || undefined,
        expiresAt: code.expiresAt || undefined,
        isUsed: code.isUsed || false,
        createdAt: code.createdAt || undefined,
        updatedAt: code.updatedAt || undefined,
      }))
    } catch (error) {
      console.error('Error in getRegistrationCodes:', error)
      throw error
    }
  }

  /**
   * Get active (unused and not expired) registration codes
   */
  async getActiveRegistrationCodes(): Promise<RegistrationCode[]> {
    try {
      const allCodes = await this.getRegistrationCodes()
      const now = new Date()

      return allCodes.filter(code => {
        if (code.isUsed) return false
        if (code.expiresAt && new Date(code.expiresAt) < now) return false
        return true
      })
    } catch (error) {
      console.error('Error in getActiveRegistrationCodes:', error)
      throw error
    }
  }

  /**
   * Delete a registration code
   */
  async deleteRegistrationCode(codeId: string): Promise<void> {
    try {
      const { errors } = await client.models.RegistrationCode.delete({
        id: codeId,
      })

      if (errors) {
        console.error('Error deleting registration code:', errors)
        throw new Error('Failed to delete registration code')
      }
    } catch (error) {
      console.error('Error in deleteRegistrationCode:', error)
      throw error
    }
  }

  /**
   * Mark a registration code as used
   */
  async markCodeAsUsed(codeId: string): Promise<RegistrationCode> {
    try {
      const { data: updatedCode, errors } = await client.models.RegistrationCode.update({
        id: codeId,
        isUsed: true,
      })

      if (errors) {
        console.error('Error updating registration code:', errors)
        throw new Error('Failed to update registration code')
      }

      return {
        id: updatedCode?.id ?? '',
        code: updatedCode?.code ?? '',
        userId: updatedCode?.userId ?? '',
        clientId: updatedCode?.clientId || undefined,
        locationId: updatedCode?.locationId || undefined,
        deviceName: updatedCode?.deviceName || undefined,
        expiresAt: updatedCode?.expiresAt || undefined,
        isUsed: updatedCode?.isUsed || false,
        createdAt: updatedCode?.createdAt || undefined,
        updatedAt: updatedCode?.updatedAt || undefined,
      }
    } catch (error) {
      console.error('Error in markCodeAsUsed:', error)
      throw error
    }
  }

  /**
   * Validate a registration code
   */
  async validateRegistrationCode(code: string): Promise<RegistrationCode | null> {
    try {
      const { data: codes, errors } = await client.models.RegistrationCode.listRegistrationCodeByCode({
        code,
      })

      if (errors) {
        console.error('Error validating registration code:', errors)
        throw new Error('Failed to validate registration code')
      }

      if (codes.length === 0) {
        return null
      }

      const registrationCode = codes[0]
      
      // Check if code is already used
      if (registrationCode.isUsed) {
        throw new Error('Registration code has already been used')
      }

      // Check if code is expired
      if (registrationCode.expiresAt && new Date(registrationCode.expiresAt) < new Date()) {
        throw new Error('Registration code has expired')
      }

      return {
        id: registrationCode.id,
        code: registrationCode.code,
        userId: registrationCode.userId,
        clientId: registrationCode.clientId || undefined,
        locationId: registrationCode.locationId || undefined,
        deviceName: registrationCode.deviceName || undefined,
        expiresAt: registrationCode.expiresAt || undefined,
        isUsed: registrationCode.isUsed || false,
        createdAt: registrationCode.createdAt || undefined,
        updatedAt: registrationCode.updatedAt || undefined,
      }
    } catch (error) {
      console.error('Error in validateRegistrationCode:', error)
      throw error
    }
  }
}

export const registrationService = new RegistrationService()
export default registrationService